/**
 * ルートプランの経路を地図へ重ねるレイヤー
 *
 * 訪問順が一目で分かることを優先し、番号付きマーカーと経路を描く。
 * 経路は直線と道なりをヘッダーのトグルで切り替える（RoutePolyline が判断する）。
 * 地図そのものはシェルが持つので、ラリーのピンと同じ地図に重ねられる。
 */

import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { CircleSlash } from 'lucide-react'
import { useMemo } from 'react'

import type { RoutePlanItem } from '@/api/types/route-plan'
import { GoogleCircleMarker } from '@/components/shared/map/google-circle-marker'
import { RoutePolyline } from '@/components/shared/map/route-polyline'
import { cn } from '@/lib/utils'

import { ROUTE_COLOR, routePath, toLocated } from './plan-route-path'

const ORIGIN_COLOR = '#3B82F6'
const SELECTED_COLOR = '#111827'
const SKIPPED_COLOR = '#9CA3AF'

type Props = {
  items: RoutePlanItem[]
  /** 上限時間に入りきらず外れたスポット。経路には入れない */
  skippedItems?: RoutePlanItem[]
  /** 絞り込みから外れたスポット。経路は保ったまま薄くする */
  mutedIds?: number[]
  origin: google.maps.LatLngLiteral | null
  /** 出発地に戻るなら経路線を閉じる */
  returnToOrigin: boolean
  /** 片道で到着地を決めているときだけ入る */
  destination: google.maps.LatLngLiteral | null
  /** 最適化が返した道なりの経路。無ければ直線で結ぶ */
  routePolyline: string | null
  selectedId: number | null
  onSelect: (item: RoutePlanItem) => void
}

export const PlanMapLayer = ({
  items,
  skippedItems = [],
  mutedIds = [],
  origin,
  returnToOrigin,
  destination,
  routePolyline,
  selectedId,
  onSelect,
}: Props) => {
  const located = useMemo(() => toLocated(items), [items])
  const locatedSkipped = useMemo(() => toLocated(skippedItems), [skippedItems])

  const path = useMemo(
    () =>
      routePath(
        located.map((entry) => entry.position),
        origin,
        returnToOrigin,
        destination,
      ),
    [located, origin, returnToOrigin, destination],
  )

  return (
    <>
      <RoutePolyline
        encoded={routePolyline}
        straightPath={path}
        strokeColor={ROUTE_COLOR}
        strokeWeight={4}
        strokeOpacity={0.8}
        dashed
        zIndex={1}
      />

      {origin && (
        <GoogleCircleMarker
          position={origin}
          radius={7}
          color="#FFFFFF"
          fillColor={ORIGIN_COLOR}
          weight={2}
          zIndex={5}
        />
      )}

      {destination && !returnToOrigin && (
        <GoogleCircleMarker
          position={destination}
          radius={7}
          color="#FFFFFF"
          fillColor={ORIGIN_COLOR}
          weight={2}
          zIndex={5}
        />
      )}

      {locatedSkipped.map(({ item, position }) => (
        <AdvancedMarker key={item.id} position={position} zIndex={8} onClick={() => onSelect(item)}>
          <div
            className="flex size-6 items-center justify-center rounded-full border-2 border-white text-white shadow-md"
            style={{ backgroundColor: item.id === selectedId ? SELECTED_COLOR : SKIPPED_COLOR }}
          >
            <CircleSlash size={12} />
          </div>
        </AdvancedMarker>
      ))}

      {located.map(({ item, position }, index) => (
        <AdvancedMarker key={item.id} position={position} zIndex={10} onClick={() => onSelect(item)}>
          <div
            className={cn(
              'flex size-7 items-center justify-center rounded-full border-2 border-white text-xs font-bold text-white shadow-md',
              mutedIds.includes(item.id) && 'opacity-30',
            )}
            style={{ backgroundColor: item.id === selectedId ? SELECTED_COLOR : ROUTE_COLOR }}
          >
            {index + 1}
          </div>
        </AdvancedMarker>
      ))}
    </>
  )
}
